import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import parseISO from 'date-fns/parseISO';
import NotificationIcon from '../../icons/Notification';
import { classNames, determineTimeStringByTimezone, } from '../../utils';
import SentinelPriorityMessage from '../SentinelPriorityMessage';
import styles from './SensorLineChartToolTip.module.css';
const CommentTypeLabel = {
    GENERAL: 'General',
    MAINTENANCE: 'Maintenance',
    CORRECTIVE_ACTION: 'Corrective Action',
    DAILY_CHECK: 'Daily Check',
    MONTHLY_CHECK: 'Monthly Check',
};
const SensorLineChartToolTip = ({ secondaryKeysMetadataMap = {}, payload, label, unit = '', locale, ianaTimeZoneCode, overrideText, }) => {
    const metadata = {
        label,
        unit,
        locale,
        ianaTimeZoneCode,
        secondaryKeysMetadataMap,
        overrideText,
    };
    if (!payload || payload.length === 0 || !payload[0]) {
        return null;
    }
    const commentPayload = payload.find((p) => p.payload.comment !== undefined);
    const notificationPayload = payload.find((p) => p.dataKey === 'notificationValue');
    if (commentPayload && commentPayload.payload.timestamp === label) {
        return renderCommentTooltips(Object.assign({ payload: commentPayload.payload, formatter: commentPayload.formatter }, metadata));
    }
    else if (notificationPayload &&
        notificationPayload.payload.timestamp === label) {
        return renderNotificationTooltips(Object.assign({ payload: notificationPayload.payload }, metadata));
    }
    else {
        return renderDataTooltips(Object.assign({ payload }, metadata));
    }
};
const renderCommentTooltips = ({ payload, formatter, unit, ianaTimeZoneCode, locale, }) => {
    const { from, value, current, commentValue, commentType, comment, userName, createdAt } = payload;
    const valueTimeString = determineTimeStringByTimezone(parseISO(from), locale.fullDateTimeFormat, ianaTimeZoneCode, true);
    const createdAtTimeString = determineTimeStringByTimezone(parseISO(createdAt), locale.fullDateTimeFormat, ianaTimeZoneCode, true);
    let commentValueEl;
    if (value && current) {
        commentValueEl =
            _jsxs("strong", { children: [Number(value).toFixed(2), " kW", _jsx("span", Object.assign({ className: styles.separator }, { children: " | " }), void 0), _jsxs("span", Object.assign({ className: styles.currentValue }, { children: [current.toFixed(2), " A"] }), void 0)] }, void 0);
    }
    else if (commentValue) {
        commentValueEl = _jsxs("strong", { children: [commentValue, ' ', unit] }, void 0);
    }
    else {
        commentValueEl = _jsxs("strong", { children: [value, ' ', unit] }, void 0);
    }
    return (_jsx("div", Object.assign({ className: styles.root }, { children: _jsxs("div", Object.assign({ className: styles.commentWrapper }, { children: [_jsx("div", Object.assign({ className: classNames(styles.secondaryText, styles.italics) }, { children: valueTimeString }), void 0), _jsx("div", Object.assign({ className: styles.commentValue }, { children: commentValueEl }), void 0), _jsxs("div", Object.assign({ className: styles.commentTextContainer }, { children: [_jsx("div", Object.assign({ className: styles.commentType }, { children: _jsx("strong", { children: CommentTypeLabel[commentType] }, void 0) }), void 0), _jsx("div", Object.assign({ className: styles.commentText }, { children: comment }), void 0), _jsxs("div", Object.assign({ className: classNames(styles.secondaryText, styles.italics) }, { children: [userName, " commented on ", createdAtTimeString] }), void 0)] }), void 0)] }), void 0) }), void 0));
};
const renderNotificationTooltips = ({ payload, ianaTimeZoneCode, locale }) => {
    const { timestamp, priorityLevel, name, message, at } = payload;
    const formattedTimeString = determineTimeStringByTimezone(new Date(at), locale.fullDateTimeFormat, ianaTimeZoneCode, true);
    return (_jsx("div", Object.assign({ className: styles.root }, { children: _jsxs("div", Object.assign({ className: styles.notificationWrapper }, { children: [_jsx("div", Object.assign({ className: styles.icon }, { children: _jsx(SentinelPriorityMessage, Object.assign({ level: priorityLevel }, { children: _jsx(NotificationIcon, { size: 20 }, void 0) }), void 0) }), void 0), _jsxs("div", Object.assign({ className: styles.content }, { children: [_jsxs("div", { children: [name && (_jsx("div", Object.assign({ className: styles.title }, { children: _jsx(SentinelPriorityMessage, Object.assign({ level: priorityLevel }, { children: name }), void 0) }), void 0)), message && _jsx("p", { children: message }, void 0)] }, void 0), _jsx("div", { children: formattedTimeString }, void 0)] }), void 0)] }), void 0) }), `notification${timestamp}`));
};
const renderDataTooltips = ({ payload, secondaryKeysMetadataMap = {}, locale, label, unit, ianaTimeZoneCode, overrideText, }) => {
    let primaryData = payload.filter((p) => p.fill !== 'url(#gradient)' &&
        p.dataKey !== 'notificationValue' &&
        p.dataKey !== 'commentValue' &&
        secondaryKeysMetadataMap[p.dataKey] === undefined);
    if (primaryData.length === 0)
        return null;
    // When the chart is showing the current(A) value, the payload will be duplicated
    if (primaryData[0].payload.current)
        primaryData = [primaryData[0]];
    const mappedPrimaryDataKeys = primaryData.map((d) => d.dataKey);
    const isAmbientTooltip = mappedPrimaryDataKeys.includes('humidity') &&
        mappedPrimaryDataKeys.includes('temperature');
    //this is to ensure temp value comes up first in ambient tooltip
    if (isAmbientTooltip) {
        primaryData = primaryData.sort((d1, d2) => {
            if (!d1.dataKey || !d2.dataKey)
                return 0;
            return d2.dataKey.localeCompare(d1.dataKey);
        });
    }
    const secondaryData = payload.filter(({ dataKey }) => secondaryKeysMetadataMap[dataKey] !== undefined);
    let timeString;
    if (primaryData[0].payload.hasOwnProperty('timestamp')) {
        timeString = determineTimeStringByTimezone(primaryData[0].payload.timestamp, locale.fullDateTimeFormat, ianaTimeZoneCode, true);
    }
    else if (primaryData[0].payload.hasOwnProperty('x')) {
        timeString = determineTimeStringByTimezone(primaryData[0].payload.x, locale.fullDateTimeFormat, ianaTimeZoneCode, true);
    }
    return (_jsxs("div", Object.assign({ className: styles.root }, { children: [timeString && (_jsx("div", Object.assign({ className: classNames(styles.secondaryText, styles.italics) }, { children: timeString }), void 0)), _jsx("div", Object.assign({ className: styles.primaryValues }, { children: primaryData.map((data) => {
                    const { dataKey, payload, stroke, formatter, value } = data;
                    const timestamp = getTimeStamp(payload);
                    return (_jsx("div", Object.assign({ style: { color: stroke } }, { children: _jsxs("strong", { children: [typeof formatter === 'function' ? formatter(value) === 'Opened' ? 'Open' : formatter(value) : value, ' ', unit] }, void 0) }), `datatooltip${timestamp}${stroke}${value}${dataKey}`));
                }) }), void 0), secondaryData.length > 0 && (_jsx("div", Object.assign({ className: styles.secondaryValues }, { children: secondaryData.map((data) => {
                    const { dataKey, payload, stroke, formatter, value } = data;
                    const timestamp = getTimeStamp(payload);
                    const metadata = secondaryKeysMetadataMap[dataKey];
                    const { entityName } = metadata;
                    return (_jsxs("div", Object.assign({ className: styles.secondaryText }, { children: [entityName, ":", ' ', typeof formatter === 'function' ? formatter(value) : value, ' ', unit] }), `datatooltip${timestamp}${stroke}${value}${dataKey}`));
                }) }), void 0)), overrideText && (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.tooltipDivider }, void 0), _jsx("div", Object.assign({ className: styles.overrideText }, { children: overrideText }), void 0)] }, void 0))] }), void 0));
};
const getTimeStamp = (payload) => {
    let timestamp = 'NONE';
    if (payload !== undefined && payload.timestamp !== undefined) {
        timestamp = payload.timestamp;
    }
    else if (payload !== undefined && payload.x !== undefined) {
        timestamp = payload.x.valueOf();
    }
    return timestamp;
};
export default SensorLineChartToolTip;
